import { Box } from "@vinomofo/components";
import { useEffect, useRef } from "react";
import { connectAutoComplete } from "react-instantsearch-dom";
import isEmpty from "lodash.isempty";
import styled from "styled-components";
import { useSearch } from "../../hooks/use-search";
import { BasicDoc, Hit } from "react-instantsearch-core";
import { ArticleCardGroup } from "../article-card-group";

type QuerySuggestion = {
  objectId: string;
  query: string;
  nb_words?: number;
  popularity?: number;
} & {
  [indexName: string]: {
    exact_nb_hits: number;
  } & Partial<Hit<BasicDoc>>;
};


const Container = styled(Box) <{ maxHeight: string; position: string; top: string }>`
  font-family: "Graphik", sans-serif;
  position: ${({ position }) => position};
  top: ${({ top }) => top};
  left: 0;
  right: 0;
  display: grid;

  background: white;
  z-index: 99;
  max-height: ${({ maxHeight }) => maxHeight};
  overflow-x: hidden;

  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  padding: 16px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

export default Container;

const Wrapper = styled.div`
  position: relative;
`;

const ImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 1;
  position: relative;
  overflow: hidden;
  cursor: pointer;
`;

const StyledImage = styled.img`
  width: 100%;  /* Ensure the image takes full width of the div */
  height: 100%; /* Ensure the image takes full height of the div */
  object-fit: cover;  /* This ensures the image will cover the div without distortion */
  object-position: center;  /* This centers the image, useful for cropping */
`;

const Title = styled.div`
  line-height: 1.2;
  height: calc(1.2em * 2);
  margin-top: 12px;
  margin-bottom: 8px;
  overflow-y: hidden;
  font-size: 18px;
  cursor: pointer;
  font-weight: 600;
  color: black;
  &:hover {
    text-decoration: underline;
  }
`;

const Excerpt = styled.div`
  position: relative;
  line-height: 1.5;
  height: calc(1.5em * 4);
  overflow: hidden;
  margin-bottom: 40px;
  font-size: 15px;
`;

const Overlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 40px;
  background: linear-gradient(0deg, #fff 0%, hsla(0, 0%, 100%, 0));
`;

const ReadMore = styled.div`
  position:absolute;
  bottom:12px;
  color: black;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    text-decoration: underline;  /* Underline text on hover */
  }
`;

interface AutoCompleteProps {
  hits: QuerySuggestion[];
  currentRefinement: string;
  close?: () => void;
  onResult?: (result: boolean) => void;
  maxHeight?: string;
  position?: "absolute" | "relative";
  top?: string;
}

export const AutoComplete = connectAutoComplete(
  ({
    hits,
    currentRefinement,
    close,
    position = "relative",
    top = "100%",
    onResult,
  }: AutoCompleteProps) => {
    if (isEmpty(currentRefinement) || hits.length === 0) {
      onResult && onResult(false)
      return null
    } else {
      onResult && onResult(true)
    }
    const containerRef = useRef<HTMLDivElement>(null);
    const { updateSearch, setShowSuggestions } = useSearch();

    const getExactNbHits = (hit: QuerySuggestion | Hit<BasicDoc>): number => {
      const indexName = process.env.GATSBY_ALGOLIA_ARTICLES_INDEX;
      return indexName ? hit[indexName]?.exact_nb_hits || 0 : 0;
    };

    const totalResults = (hit: QuerySuggestion | Hit<BasicDoc>): number | string => {
      return getExactNbHits(hit) || "";
    };

    useEffect(() => {
      const handleClickOutside = (event: Event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setShowSuggestions(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [close]);

    return (
     <ArticleCardGroup hits={hits} containerRef={containerRef} position={position} top={top} rowNumber={4}/>
    );
  }
);
